import type { PaginationMeta } from "@nasam/kernel";
import type {
  GprsCatalogCommand,
  GprsCommand,
  GprsCommandStatus,
  GprsCommandsListQuery,
  GprsCommandsRepository,
  GprsCommandsSortBy,
  GprsNamedRef,
} from "../domain";

const MOCK_COMPANY: GprsNamedRef = {
  id: "mock-account",
  nameEn: "Nasam Logistics",
  nameAr: "نسم للخدمات اللوجستية",
};

/** How long a mock PENDING command waits before it resolves. */
const MOCK_RESOLVE_MIN_MS = 5_000;
const MOCK_RESOLVE_MAX_MS = 15_000;

function command(
  code: string,
  nameEn: string,
  nameAr: string,
  parameters: GprsCatalogCommand["parameters"] = [],
): GprsCatalogCommand {
  return {
    id: `command-${code.toLowerCase()}`,
    code,
    nameEn,
    nameAr,
    parameters,
    supportedDevices: null,
  };
}

/** The story's Available Commands, with the API's parameter shapes. */
export const MOCK_GPRS_CATALOG: readonly GprsCatalogCommand[] = [
  command("REBOOT", "Reboot", "إعادة التشغيل"),
  command("GET_POSITION", "Get Position", "جلب الموقع"),
  command("SET_APN", "Set APN", "ضبط APN", [
    { name: "apn", labelEn: "APN", labelAr: "APN", kind: "TEXT", required: true, minValue: 1, maxValue: 64, pattern: null },
    { name: "username", labelEn: "Username", labelAr: "اسم المستخدم", kind: "TEXT", required: false, minValue: null, maxValue: 32, pattern: null },
  ]),
  command("CHANGE_SERVER", "Change Server", "تغيير الخادم", [
    { name: "host", labelEn: "Server domain or IP", labelAr: "نطاق الخادم أو عنوان IP", kind: "TEXT", required: true, minValue: 1, maxValue: 55, pattern: "^[A-Za-z0-9.-]+$" },
    { name: "port", labelEn: "Port", labelAr: "المنفذ", kind: "INTEGER", required: true, minValue: 1, maxValue: 65535, pattern: null },
  ]),
  command("CONFIGURE_REPORTING_INTERVAL", "Configure Reporting Interval", "ضبط فترة الإرسال", [
    { name: "intervalSeconds", labelEn: "Interval (seconds)", labelAr: "الفترة (ثوانٍ)", kind: "INTEGER", required: true, minValue: 1, maxValue: 86400, pattern: null },
  ]),
  command("ENABLE_IGNITION", "Enable Ignition", "تفعيل الإشعال"),
  command("DISABLE_IGNITION", "Disable Ignition", "تعطيل الإشعال"),
  command("FIRMWARE_UPGRADE", "Firmware Upgrade", "تحديث البرنامج الثابت", [
    { name: "firmwareUrl", labelEn: "Firmware URL", labelAr: "رابط البرنامج الثابت", kind: "TEXT", required: true, minValue: 1, maxValue: 255, pattern: null },
  ]),
  command("REQUEST_SENSORS", "Request Sensors", "طلب الحساسات"),
  command("RESTART_GSM", "Restart GSM", "إعادة تشغيل GSM"),
  command("RESET_DEVICE", "Reset Device", "إعادة ضبط الجهاز"),
];

function kindOf(code: string): GprsCommand["command"] {
  const entry = MOCK_GPRS_CATALOG.find((item) => item.code === code)!;
  return { typeId: entry.id, code: entry.code, nameEn: entry.nameEn, nameAr: entry.nameAr };
}

function seedCommand(
  index: number,
  imei: string,
  code: string,
  response: string | null,
  executionMs: number | null,
  status: GprsCommandStatus,
  failureReason: string | null = null,
): GprsCommand {
  // A day apart, so the date filter and newest-first order have something to do.
  const createdAt = new Date(Date.now() - index * 86_400_000).toISOString();
  return {
    id: `gprs-command-${String(index)}`,
    batchId: `batch-${String(index)}`,
    company: MOCK_COMPANY,
    device: { id: `device-${imei}`, code: `DEV-${imei.slice(-6)}`, imei },
    command: kindOf(code),
    params: {},
    payload: code.toLowerCase(),
    status,
    failureReason,
    response,
    executionMs,
    sentAt: createdAt,
    respondedAt: executionMs === null ? null : createdAt,
    createdAt,
  };
}

// Mirrors the rows in the Figma "GPRS Commands" list.
function seedCommands(): GprsCommand[] {
  return [
    seedCommand(1, "358241000103", "CONFIGURE_REPORTING_INTERVAL", "TEMP:92C,FUEL:85%", 15_000, "SUCCESSFUL"),
    seedCommand(2, "358241000106", "REBOOT", "REBOOT_ACK,0", 45_000, "FAILED", "failed"),
    seedCommand(3, "358241000104", "REBOOT", null, null, "FAILED", "offline"),
    seedCommand(4, "358241000102", "CONFIGURE_REPORTING_INTERVAL", "REPORTING_INTERVAL_OK", 15_000, "SUCCESSFUL"),
    seedCommand(5, "358241000105", "REBOOT", "REBOOT_ACK,0", 75_000, "FAILED", "failed"),
    seedCommand(6, "358241000101", "REQUEST_SENSORS", null, null, "FAILED", "unsupported"),
    seedCommand(7, "358241000107", "RESTART_GSM", "GSM_RESTART_ACK", 1_560, "SUCCESSFUL"),
    seedCommand(8, "358241000108", "GET_POSITION", "LAT:24.71,LNG:46.68", 20_000, "SUCCESSFUL"),
    seedCommand(9, "358241000109", "GET_POSITION", null, null, "TIMED_OUT", "timeout"),
    seedCommand(10, "358241000110", "SET_APN", "APN_OK", 12_000, "SUCCESSFUL"),
    seedCommand(11, "358241000111", "DISABLE_IGNITION", "IGN_OFF_ACK", 25_000, "SUCCESSFUL"),
    seedCommand(12, "358241000112", "FIRMWARE_UPGRADE", null, null, "TIMED_OUT", "timeout"),
  ];
}

function paginationMeta(totalCount: number, page: number, limit: number): PaginationMeta {
  const totalPages = Math.max(1, Math.ceil(totalCount / limit));
  return {
    totalCount,
    currentPage: page,
    totalPages,
    firstPageUrl: "",
    lastPageUrl: "",
    nextPageUrl: page < totalPages ? "" : null,
    previousPageUrl: page > 1 ? "" : null,
  };
}

function matchesSearch(row: GprsCommand, search: string): boolean {
  const needle = search.trim().toLowerCase();
  if (needle.length === 0) return true;
  return [
    row.company?.nameEn,
    row.company?.nameAr,
    row.device.imei,
    row.device.code,
    row.command?.nameEn,
    row.command?.nameAr,
    row.payload,
    row.response,
  ].some((value) => (value ?? "").toLowerCase().includes(needle));
}

function sortValue(row: GprsCommand, sortBy: GprsCommandsSortBy): string | number {
  switch (sortBy) {
    case "createdAt":
      return row.createdAt;
    case "status":
      return row.status;
    case "executionMs":
      return row.executionMs ?? Number.POSITIVE_INFINITY;
    case "commandNameEn":
      return row.command?.nameEn ?? row.payload ?? "";
    case "commandNameAr":
      return row.command?.nameAr ?? row.payload ?? "";
    case "deviceCode":
      return row.device.code;
    case "imei":
      return row.device.imei;
    case "accountNameEn":
      return row.company?.nameEn ?? "";
    case "accountNameAr":
      return row.company?.nameAr ?? "";
  }
}

function compare(a: GprsCommand, b: GprsCommand, sortBy: GprsCommandsSortBy): number {
  const left = sortValue(a, sortBy);
  const right = sortValue(b, sortBy);
  return typeof left === "number" && typeof right === "number"
    ? left - right
    : String(left).localeCompare(String(right));
}

function inRange(value: string, from: string | undefined, to: string | undefined): boolean {
  const time = Date.parse(value);
  if (from !== undefined && time < Date.parse(from)) return false;
  if (to !== undefined && time > Date.parse(to)) return false;
  return true;
}

/** A random final outcome for a mock PENDING command. */
function resolveMockOutcome(row: GprsCommand, elapsedMs: number): GprsCommand {
  const roll = Math.random();
  const respondedAt = new Date().toISOString();
  const code = row.command?.code ?? "CUSTOM";
  if (roll < 0.7) {
    return { ...row, status: "SUCCESSFUL", response: `${code}_ACK,1`, executionMs: Math.round(elapsedMs), respondedAt };
  }
  if (roll < 0.85) {
    return { ...row, status: "FAILED", failureReason: "failed", response: `${code}_NACK,0`, executionMs: Math.round(elapsedMs), respondedAt };
  }
  return { ...row, status: "TIMED_OUT", failureReason: "timeout" };
}

/** In-memory GPRS commands for tests and offline work, in the API's shapes. */
export function createGprsCommandsMockRepository(): GprsCommandsRepository {
  const store = new Map<string, GprsCommand>(seedCommands().map((row) => [row.id, row]));
  let nextId = store.size + 1;
  let nextBatch = 1;

  return {
    list(query: GprsCommandsListQuery = {}) {
      const page = query.page ?? 1;
      const limit = query.limit ?? 10;
      const sortBy = query.sortBy ?? "createdAt";
      const direction = (query.sort ?? "desc") === "asc" ? 1 : -1;
      const rows = [...store.values()]
        .filter((row) => query.search === undefined || matchesSearch(row, query.search))
        .filter((row) => query.status === undefined || row.status === query.status)
        .filter((row) => query.batchId === undefined || row.batchId === query.batchId)
        .filter((row) => inRange(row.createdAt, query.fromCreatedAt, query.toCreatedAt))
        .sort((a, b) => direction * compare(a, b, sortBy));
      const start = (page - 1) * limit;
      return Promise.resolve({
        data: rows.slice(start, start + limit),
        meta: paginationMeta(rows.length, page, limit),
      });
    },

    get(id) {
      const row = store.get(id);
      return row === undefined
        ? Promise.reject(new Error("GPRS command not found."))
        : Promise.resolve(row);
    },

    catalog(query = {}) {
      const targets = query.deviceIds?.length ?? (query.accountIds === undefined ? 0 : 3);
      return Promise.resolve(
        MOCK_GPRS_CATALOG.map((entry) => ({
          ...entry,
          supportedDevices: targets === 0 ? null : targets,
        })),
      );
    },

    send(input) {
      const createdAt = new Date().toISOString();
      const batchId = `mock-batch-${String(nextBatch++)}`;
      const deviceIds = "deviceIds" in input ? input.deviceIds : ["device-all-1", "device-all-2"];
      const kind =
        "commandTypeId" in input
          ? (MOCK_GPRS_CATALOG.find((entry) => entry.id === input.commandTypeId) ?? null)
          : null;
      for (const deviceId of deviceIds) {
        const row: GprsCommand = {
          id: `gprs-command-${String(nextId++)}`,
          batchId,
          company: MOCK_COMPANY,
          device: { id: deviceId, code: deviceId.toUpperCase(), imei: deviceId.replace(/\D/g, "") },
          command:
            kind === null
              ? null
              : { typeId: kind.id, code: kind.code, nameEn: kind.nameEn, nameAr: kind.nameAr },
          params:
            "params" in input
              ? Object.fromEntries(
                  Object.entries(input.params).map(([name, value]) => [name, String(value)]),
                )
              : {},
          payload: "customText" in input ? input.customText : (kind?.code.toLowerCase() ?? null),
          status: "PENDING",
          failureReason: null,
          response: null,
          executionMs: null,
          sentAt: createdAt,
          respondedAt: null,
          createdAt,
        };
        store.set(row.id, row);
        // Each device answers on its own schedule -- tracked independently.
        const delay = MOCK_RESOLVE_MIN_MS + Math.random() * (MOCK_RESOLVE_MAX_MS - MOCK_RESOLVE_MIN_MS);
        setTimeout(() => {
          const current = store.get(row.id);
          if (current?.status === "PENDING") store.set(row.id, resolveMockOutcome(current, delay));
        }, delay);
      }
      return Promise.resolve({
        batchId,
        total: deviceIds.length,
        pending: deviceIds.length,
        failed: 0,
        message: null,
      });
    },
  };
}
